
import {  FormEvent, useEffect, useState } from 'react'
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../../../hooks/useAlert';
import { ProfessionalFormDto } from '../types';
import { getProfessionalById, saveProfessional, updateProfessional } from '../api/professionalAPI';
import { ApiErrorResponse } from '../../../types/ApiErrorResponse';
import { getProfessionaFormSchema } from '../schema/ProfessionalSchema';
import { Row, Col, Form } from 'react-bootstrap';
import AlertMessage from '../../../components/AlertMessage';
import { GenericSaveButton } from '../../../components/common/GenericSaveButton';
import { useClaimProfessionalTypeSelect } from '../../admin/Dictionaries/hooks/getClaimProfessionalTypeSelectByClient';
import { useServiceIndicatorSelect } from '../../admin/Dictionaries/hooks/getServiceIndicatorSelectsByClient';


export default function ProfessionalForm() {
    const { t } = useTranslation(['common', 'professional']);
    const navigate = useNavigate();

    const { professionalId } = useParams<{ professionalId: string }>();
    const isEditMode = Boolean(professionalId);


    const { data: serviceIndicators } = useServiceIndicatorSelect({});
    const { data: claimProfessionalTypes } = useClaimProfessionalTypeSelect({});
    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });

    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
    const [professionalForm, setProfessionalForm] = useState<ProfessionalFormDto>({
        id: 0,
        nameAndSurname: "",
        serviceIndicatorId: null,
        registeredNumber: "",
        typeProfessionalId :null,
        email: ""
    })

    const changeInputField = <K extends keyof ProfessionalFormDto>(
        field: K,
        value: ProfessionalFormDto[K]
    ) => {
        setProfessionalForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    useEffect(() => {
        if (professionalId) {
            getProfessionalById(professionalId)
                .then(data => setProfessionalForm(data))
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                });
        }
    }, [professionalId]);

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        const schema = getProfessionaFormSchema(t);

        try {
            await schema.validate(professionalForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            err.inner?.forEach((error: any) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            setErrors(validationErrors);
            return;
        }


        try {
            if (isEditMode && professionalId) {
                await updateProfessional(professionalId, professionalForm);
                navigate('/professionals', {
                    state: { message: t("professional:messageUpdatedProfessionalSuccessfully"), shouldRefresh: true }
                });
                
            } else {

                await saveProfessional(professionalForm);
                navigate('/professionals', {
                    state: { message: t("professional:messageCreatedProfessionalSuccessfully"), shouldRefresh: true }
                });
            }

        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message
            showAlert({
                message: errorMessage,
                type: "danger",
                duration: 5000,
            });

        }
    }

  

    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>{professionalId ? t("professional:editProfessionalTitle") : t("professional:buttonNewProfessional")}</h2>
                </Col>
                <Col xs={12} md={4} className="text-end mt-2 mt-md-0">
                    <GenericSaveButton
                        onClick={() => handleSubmit()}
                        onCancelClick={() => navigate('/professionals')}
                        cancelLabel={isEditMode ? t("common:buttons.buttonCancel") : t("common:buttons.buttonBack")}
                    />
                </Col>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <Row>
                <Col>
                    <Form>
                        <Row>
                            <Col>
                                <Row>
                                    <Col xs={6} md={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                {t("professional:form.inputProfessionalId")}
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={professionalForm.id ?? ""}
                                                disabled={true}
                                                
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={6} md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("professional:form.inputProfessionalRegisteredNumber")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={professionalForm.registeredNumber ?? ""}
                                                onChange={(e) =>
                                                    changeInputField("registeredNumber", e.target.value)
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} md={12}>
                                        <Form.Group>
                                            <Form.Label>
                                                {t("professional:form.inputProfessionalName")}
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={professionalForm.nameAndSurname ?? ""}
                                                isInvalid={!!errors["nameAndSurname"]}
                                                onChange={(e) =>
                                                    changeInputField("nameAndSurname", e.target.value)
                                                }
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.nameAndSurname || ""}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} md={12}>
                                        <Form.Group>
                                            <Form.Label>{t("professional:form.inputProfessionalEmail")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={professionalForm.email ?? ""}
                                                onChange={(e) =>
                                                    changeInputField("email", e.target.value)
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} md={12}>
                                        <Form.Label>{t("professional:form.inputProfessionalService")}</Form.Label>
                                        <Form.Select
                                            className="form-control"
                                            value={professionalForm.serviceIndicatorId ?? ""}
                                            onChange={(e) =>
                                                changeInputField("serviceIndicatorId", parseInt(e.target.value))
                                            }
                                        >
                                            <option value=""></option>
                                            {serviceIndicators?.map((serviceIndicator) => (
                                                <option key={serviceIndicator.id} value={serviceIndicator.id}>
                                                    {serviceIndicator.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>
                                <Row>
                                    

                                    <Col xs={12} md={12}>
                                        <Form.Label>{t("professional:form.inputProfessionalCategory")}</Form.Label>
                                        <Form.Select
                                            className="form-control"
                                            value={professionalForm.typeProfessionalId ?? ""}
                                            onChange={(e) =>
                                                changeInputField( "typeProfessionalId", parseInt(e.target.value))
                                            }
                                        >
                                            <option value=""></option>
                                            {claimProfessionalTypes?.map((type) => (
                                                <option key={type.id} value={type.id}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </>
    );
}






